import { HiCheckCircle, HiExclamationCircle, HiXCircle } from "react-icons/hi";
import { LuCircleDot, LuLoader2 } from "react-icons/lu";
import { STATUS } from "../../../services/agent-eval";

/**
 * Presentational pieces shared by the Agent Tests page and Copilot's testing
 * card.
 */

// ─── Status ───────────────────────────────────────────────────────────────────
// Errored is deliberately not styled as a failure. A case that could not run is
// not evidence about the agent, and colouring it red makes an infrastructure
// problem look like a regression — which is exactly the wrong thing to tell
// someone reading a results page.
const STATUS_STYLES = {
	[STATUS.PASSED]: {
		label: "Passed",
		icon: HiCheckCircle,
		className: "text-green-600 bg-green-50 dark:bg-green-900/20",
	},
	[STATUS.FAILED]: {
		label: "Failed",
		icon: HiXCircle,
		className: "text-red-600 bg-red-50 dark:bg-red-900/20",
	},
	[STATUS.ERROR]: {
		label: "Could not run",
		icon: HiExclamationCircle,
		className: "text-amber-600 bg-amber-50 dark:bg-amber-900/20",
	},
	[STATUS.RUNNING]: {
		label: "Running",
		icon: LuLoader2,
		className: "text-blue-600 bg-blue-50 dark:bg-blue-900/20",
	},
	[STATUS.PENDING]: {
		label: "Pending",
		icon: LuCircleDot,
		className: "text-gray-500 bg-gray-50",
	},
	notRun: {
		label: "Not run yet",
		icon: LuCircleDot,
		className: "text-gray-500 bg-gray-50",
	},
	[STATUS.CANCELLED]: {
		label: "Not run",
		icon: LuCircleDot,
		className: "text-gray-500 bg-gray-50",
	},
	[STATUS.SKIPPED]: {
		label: "Skipped",
		icon: LuCircleDot,
		className: "text-gray-500 bg-gray-50",
	},
};

export function StatusPill({ status, className = "" }) {
	const style = STATUS_STYLES[status] || STATUS_STYLES[STATUS.PENDING];
	const Icon = style.icon;
	const spin = status === STATUS.RUNNING ? "animate-spin" : "";

	return (
		<span
			className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${style.className} ${className}`}
		>
			<Icon className={`h-3.5 w-3.5 ${spin}`} />
			{style.label}
		</span>
	);
}

// ─── Transcript ───────────────────────────────────────────────────────────────

/**
 * What actually happened, turn by turn.
 *
 * This is the thing a person opens a failed case to see, so it shows the
 * conversation as a conversation rather than as a log: the user's message, what
 * came back, and the buttons offered.
 */
export function Transcript({ turns = [], toolCalls = [] }) {
	if (!turns.length) {
		return <p className="text-sm text-gray-400 italic">No turns ran.</p>;
	}

	return (
		<div className="space-y-3">
			{turns.map((turn, i) => (
				<div key={i} className="space-y-1.5">
					<div className="flex justify-end">
						<div className="max-w-[80%] bg-primary text-white rounded-2xl rounded-br-sm px-3 py-2 text-sm">
							{turn.message}
						</div>
					</div>

					{turn.error && (
						<div className="flex justify-start">
							<div className="max-w-[80%] bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 rounded-2xl rounded-bl-sm px-3 py-2 text-sm font-mono">
								{turn.error}
							</div>
						</div>
					)}

					{(turn.replies || []).map((reply, j) => (
						<div key={j} className="flex justify-start">
							<div className="max-w-[80%] bg-gray-100 text-gray-800 rounded-2xl rounded-bl-sm px-3 py-2 text-sm whitespace-pre-wrap break-words">
								{reply}
							</div>
						</div>
					))}

					{(turn.buttons || []).length > 0 && (
						<div className="flex flex-wrap gap-1.5 pl-1">
							{turn.buttons.map((button, j) => (
								<span
									key={j}
									className="text-xs border border-primary/40 text-primary rounded-full px-2 py-0.5"
								>
									{button}
								</span>
							))}
						</div>
					)}

					<p className="text-[11px] text-gray-400 pl-1">{turn.seconds}s</p>
				</div>
			))}
			{toolCalls.length > 0 && (
				<p className="text-xs text-gray-500">
					Used: {[...new Set(toolCalls.map((call) => call.name))].join(", ")}
				</p>
			)}
		</div>
	);
}

// ─── Empty state ──────────────────────────────────────────────────────────────

export function EmptyState({ icon: Icon, title, body, action }) {
	return (
		<div className="flex flex-col items-center justify-center py-16 text-center">
			{Icon && <Icon className="h-10 w-10 text-gray-300 mb-3" />}
			<p className="text-gray-700 font-medium">{title}</p>
			{body && <p className="text-sm text-gray-500 mt-1 max-w-md">{body}</p>}
			{action && <div className="mt-4">{action}</div>}
		</div>
	);
}
